import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useDraggingContext } from '../useDraggingContext';
import { useShowSuccessToast } from './useShowSuccessToast';

export function useWidgetOrder<T extends { id: string }>(storageKey: string, initialData: T[]) {
  const { toggleDraggable, isDraggable } = useDraggingContext();
  const showSuccessToast = useShowSuccessToast();
  const { t } = useTranslation();

  const enabledStorageKey = `${storageKey}_enabled`;

  const reconcileOrder = (savedIds: string[]): T[] => {
    // Create a map of current initialData for quick lookup with all current properties
    const initialDataMap = new Map(initialData.map((item) => [item.id, item]));

    const existingItems = savedIds.map((id) => initialDataMap.get(id)).filter(Boolean) as T[];
    const existingIds = new Set(savedIds);
    const missingItems = initialData.filter((item) => !existingIds.has(item.id));
    let allItems = [...existingItems, ...missingItems];

    // Move alert cards to the front (highest priority)
    // Support both legacy pattern (id === '1') and new alert patterns (HVACFilterAlert, etc.)
    const alertCards = allItems.filter(
      (item) => item.id === '1' || item.id.includes('Alert') || item.id.includes('alert'),
    );
    const nonAlertCards = allItems.filter(
      (item) => item.id !== '1' && !item.id.includes('Alert') && !item.id.includes('alert'),
    );
    allItems = [...alertCards, ...nonAlertCards];

    return allItems;
  };

  const [items, setItems] = useState<T[]>(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      const ids: string[] = JSON.parse(saved);
      return reconcileOrder(ids);
    }
    return initialData;
  });

  const [enabledIds, setEnabledIds] = useState<Set<string>>(() => {
    const saved = localStorage.getItem(enabledStorageKey);
    if (saved) {
      const ids: string[] = JSON.parse(saved);
      return new Set(ids);
    }
    return new Set(initialData.map((item) => item.id));
  });

  const [savedOrder, setSavedOrder] = useState(items.map((item) => item.id));

  const displayItems = isDraggable ? items : items.filter((item) => enabledIds.has(item.id));

  useEffect(() => {
    const ids = items.map((item) => item.id);
    localStorage.setItem(storageKey, JSON.stringify(ids));
  }, [items.length, storageKey]);

  useEffect(() => {
    const idsArray = Array.from(enabledIds);
    localStorage.setItem(enabledStorageKey, JSON.stringify(idsArray));
  }, [enabledIds, enabledStorageKey]);

  useEffect(() => {
    setItems((prevItems) => {
      const prevIds = prevItems.map((item) => item.id);
      const newIds = initialData.map((item) => item.id);

      // If the structure changed (different IDs or order), reconcile with saved order
      if (prevIds.length !== newIds.length || prevIds.some((id, idx) => id !== newIds[idx])) {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
          const ids: string[] = JSON.parse(saved);
          return reconcileOrder(ids);
        }
        return initialData;
      }

      // If structure is the same but data might have changed (e.g., timestamp updates),
      // update items with latest data while preserving order
      const initialDataMap = new Map(initialData.map((item) => [item.id, item]));
      return prevItems.map((prevItem) => initialDataMap.get(prevItem.id) || prevItem);
    });
    setEnabledIds((prev) => {
      const newSet = new Set(prev);
      initialData.forEach((item) => newSet.add(item.id));
      return newSet;
    });
  }, [initialData, storageKey]);

  const handleItemsReordered = (reorderedItems: T[]) => {
    setItems(reorderedItems);
  };

  const handleStartDragging = () => {
    setSavedOrder(items.map((item) => item.id));
    toggleDraggable();
  };

  const handleSaveOrder = () => {
    const ids = items.map((item) => item.id);
    localStorage.setItem(storageKey, JSON.stringify(ids));
    setSavedOrder(ids);
    toggleDraggable();
    showSuccessToast(t('common.changesApplied'), t('common.changesAppliedDescription'));
  };

  const handleCancelOrder = () => {
    setItems(reconcileOrder(savedOrder));
    toggleDraggable();
  };

  const toggleItemEnabled = (id: string) => {
    setEnabledIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  const setItemEnabled = (id: string, enabled: boolean) => {
    setEnabledIds((prev) => {
      const newSet = new Set(prev);
      if (enabled) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  };

  const isItemEnabled = (id: string) => enabledIds.has(id);

  return {
    items: displayItems,
    setItems,
    enabledIds,
    setEnabledIds,
    handleItemsReordered,
    handleStartDragging,
    handleSaveOrder,
    handleCancelOrder,
    toggleItemEnabled,
    setItemEnabled,
    isItemEnabled,
  };
}
